import { useState } from "react";
import { useApp } from "../../context";
import { darkTheme, lightTheme } from "./theme";
import Dashboard from "./Dashboard";
import Subscribers from "./Subscribers";
import Plans from "./Plans";
import Revenue from "./Revenue";
import Traffic from "./Traffic";
import Demographics from "./Demographics";
import Chats from "./Chats";
import Content from "./Content";
import Engagement from "./Engagement";
import Analytics from "./Analytics";
import Events from "./Events";
import Verification from "./Verification";
import Reports from "./Reports";
import Users from "./Users";
import Wallet from "./Wallet";
import Settings from "./Settings";

const NAV = [
  { id: "dashboard",     icon: "📊", label: "Dashboard" },
  { id: "subscribers",   icon: "👥", label: "Subscribers" },
  { id: "plans",         icon: "💳", label: "Plans & Pricing" },
  { id: "revenue",       icon: "💰", label: "Revenue" },
  { id: "traffic",       icon: "🚦", label: "Traffic" },
  { id: "demographics",  icon: "🌍", label: "Demographics" },
  { id: "chats",         icon: "💬", label: "Chats" },
  { id: "content",       icon: "📝", label: "Content" },
  { id: "engagement",    icon: "🤝", label: "Engagement" },
  { id: "analytics",     icon: "📈", label: "Analytics" },
  { id: "events",        icon: "🎉", label: "Events" },
  { id: "verification",  icon: "🛡️", label: "Verification" },
  { id: "reports",       icon: "⚠️", label: "Reports" },
  { id: "users",         icon: "👤", label: "All Users" },
  { id: "wallet",        icon: "🪙", label: "GT Tokens" },
{ id: "settings",      icon: "⚙️", label: "Settings" },
];

export default function AdminPanel() {
  const { user } = useApp();
  const [section, setSection] = useState("dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("gc_admin_dark");
    return saved !== null ? saved === "true" : true;
  });

  const t = isDark ? darkTheme : lightTheme;

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem("gc_admin_dark", String(next));
  };

  if (!user?.isAdmin) return (
    <div style={{ minHeight: "100vh", background: t.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>🚫</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: t.text }}>Access Denied</div>
        <div style={{ fontSize: 14, color: t.sub, marginTop: 8 }}>Admin access only</div>
      </div>
    </div>
  );

  const W = collapsed ? 60 : 220;

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: t.bg, fontFamily: "'DM Sans', sans-serif", color: t.text }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* ── Sidebar ── */}
      <div style={{ width: W, background: t.sidebar, borderRight: `1px solid ${t.border}`, display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 100, transition: "width 0.2s", overflow: "hidden", boxShadow: isDark ? "none" : "2px 0 12px rgba(0,0,0,0.06)" }}>

        {/* Logo */}
        <div style={{ padding: collapsed ? "20px 0" : "20px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between" }}>
          {!collapsed && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: t.pri, letterSpacing: 0.5 }}>Community Platform</div>
              <div style={{ fontSize: 10, color: t.sub, marginTop: 2 }}>community.geilstore.com</div>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} style={{ background: "none", border: "none", color: t.sub, cursor: "pointer", fontSize: 16, padding: 4, flexShrink: 0 }}>
            {collapsed ? "→" : "←"}
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "12px 8px", overflowY: "auto", scrollbarWidth: "none" }}>
          {NAV.map(item => {
            const active = section === item.id;
            return (
              <button key={item.id} onClick={() => setSection(item.id)} title={collapsed ? item.label : ""}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: collapsed ? "10px 0" : "9px 12px", justifyContent: collapsed ? "center" : "flex-start", borderRadius: 8, border: "none", background: active ? `${t.pri}18` : "transparent", color: active ? t.pri : t.sub, fontSize: 13, fontWeight: active ? 700 : 500, cursor: "pointer", marginBottom: 2, transition: "all 0.15s", whiteSpace: "nowrap" }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && item.label}
                {!collapsed && active && <div style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: 3, background: t.pri }} />}
              </button>
            );
          })}
        </nav>

        {/* Theme toggle + User */}
        {!collapsed && (
          <div style={{ padding: "10px 16px", borderTop: `1px solid ${t.border}` }}>
            <button onClick={toggleTheme}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, cursor: "pointer", marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: t.sub, fontWeight: 600 }}>{isDark ? "🌙 Dark Mode" : "☀️ Light Mode"}</span>
              <div style={{ width: 36, height: 20, borderRadius: 10, background: isDark ? t.pri : t.border, position: "relative", transition: "background 0.2s" }}>
                <div style={{ position: "absolute", top: 3, left: isDark ? 18 : 3, width: 14, height: 14, borderRadius: 7, background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
              </div>
            </button>
          </div>
        )}

        <div style={{ padding: collapsed ? "12px 0" : "12px 16px", borderTop: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start" }}>
          <div style={{ width: 32, height: 32, borderRadius: 16, background: `linear-gradient(135deg, ${t.pri}, #a23ad1)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{user?.emoji || "👤"}</div>
          {!collapsed && (
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: t.sub }}>Administrator</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ marginLeft: W, flex: 1, minHeight: "100vh", transition: "margin-left 0.2s" }}>

        {/* Top bar */}
        <div style={{ background: t.sidebar, borderBottom: `1px solid ${t.border}`, padding: "12px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50, boxShadow: isDark ? "none" : "0 1px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 13, color: t.sub }}>
            <span style={{ color: t.pri, fontWeight: 700 }}>community.geilstore.com</span>
            <span style={{ margin: "0 8px" }}>—</span>
            live from MySQL
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {collapsed && (
              <button onClick={toggleTheme} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${t.border}`, background: t.hover, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {isDark ? "☀️" : "🌙"}
              </button>
            )}
            <div style={{ fontSize: 11, color: "#10b981", background: "#10b98115", border: "1px solid #10b98130", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>
              🟢 Live
            </div>
            <a href="https://community.geilstore.com" target="_blank" rel="noopener noreferrer"
              style={{ padding: "7px 14px", borderRadius: 8, background: t.pri, color: "#fff", fontSize: 12, fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", gap: 5 }}>
              ↗ Open Site
            </a>
          </div>
        </div>

        {/* Page content */}
        <div style={{ padding: "28px 32px", minHeight: "calc(100vh - 57px)" }}>
          {section === "dashboard"    && <Dashboard    onNavigate={setSection} t={t} isDark={isDark} />}
          {section === "subscribers"  && <Subscribers  t={t} isDark={isDark} />}
          {section === "plans"        && <Plans         t={t} isDark={isDark} />}
          {section === "revenue"      && <Revenue       t={t} isDark={isDark} />}
          {section === "traffic"      && <Traffic       t={t} isDark={isDark} />}
          {section === "demographics" && <Demographics  t={t} isDark={isDark} />}
          {section === "chats"        && <Chats         t={t} isDark={isDark} />}
          {section === "content"      && <Content       t={t} isDark={isDark} />}
          {section === "engagement"   && <Engagement    t={t} isDark={isDark} />}
          {section === "analytics"    && <Analytics     t={t} isDark={isDark} />}
          {section === "events"       && <Events        t={t} isDark={isDark} />}
          {section === "verification" && <Verification  t={t} isDark={isDark} />}
          {section === "reports"      && <Reports       t={t} isDark={isDark} />}
          {section === "users"        && <Users         t={t} isDark={isDark} />}
          {section === "wallet"       && <Wallet         t={t} isDark={isDark} />}
          {section === "settings"     && <Settings       t={t} isDark={isDark} />}
        </div>
      </div>
    </div>
  );
}